import { Box, Checkbox, FormControlLabel } from "@mui/material"
import type { FC } from "react"
import { useMemo } from "react"

import { AerosTextField } from "@/components/UiKit/AerosTextField/AerosTextField"
import type { Invoice } from "@/graphql/codegen/graphql"
import { useExpenseAccountOptions } from "@/screens/Accounting/subfeatures/Invoices/hooks/useExpenseAccountOptions"
import { useGetVendorPaymentTerm } from "@/screens/Accounting/subfeatures/Invoices/hooks/useGetVendorPaymentTerm"
import { calculateInvoiceExpenseTotal, formatInvoiceCurrency } from "@/screens/Accounting/subfeatures/Invoices/utils"
import { formatDate } from "@/utils/date"

interface InvoiceReviewDetailsSectionProps {
  invoice: Invoice
}

export const InvoiceReviewDetailsSection: FC<InvoiceReviewDetailsSectionProps> = ({ invoice }) => {
  const { expenseAccounts } = useExpenseAccountOptions()
  const { vendorPaymentTermName } = useGetVendorPaymentTerm(invoice.vendorCompanyId)

  const expenseAccountIdToLabel = useMemo(
    () => new Map(expenseAccounts.map((account) => [account.id, account.label])),
    [expenseAccounts]
  )

  const total = calculateInvoiceExpenseTotal(invoice)

  return (
    <Box className='flex flex-col gap-6'>
      <AerosTextField
        label='Vendor Name'
        value={invoice.vendorCompany?.name ?? ""}
        disabled
        required
        data-testid='accounting.invoice-review-dialog.vendor-field'
      />

      <Box className='grid grid-cols-2 gap-4'>
        <AerosTextField
          label='Payment Terms'
          value={vendorPaymentTermName ?? ""}
          disabled
          data-testid='accounting.invoice-review-dialog.payment-terms-field'
        />
        <AerosTextField
          label='Vendor Invoice #'
          value={invoice.invoiceNumber}
          disabled
          required
          data-testid='accounting.invoice-review-dialog.invoice-number-field'
        />
      </Box>

      <Box className='grid grid-cols-3 gap-4'>
        <AerosTextField
          label='Invoice Date or Date Received'
          value={invoice.invoiceDate ? formatDate(invoice.invoiceDate) : ""}
          disabled
          required
          data-testid='accounting.invoice-review-dialog.invoice-date-field'
        />
        <AerosTextField
          label='Accounting Period'
          value={invoice.accountingPeriod ?? ""}
          disabled
          data-testid='accounting.invoice-review-dialog.accounting-period-field'
        />
        <AerosTextField
          label='Due Date'
          value={invoice.dueDate ? formatDate(invoice.dueDate) : ""}
          disabled
          required
          data-testid='accounting.invoice-review-dialog.due-date-field'
        />
      </Box>

      <Box className='grid grid-cols-2 items-end gap-4'>
        <AerosTextField
          label='Total Amount to Pay'
          value={formatInvoiceCurrency(total)}
          disabled
          required
          data-testid='accounting.invoice-review-dialog.total-amount-field'
        />
        <Box className='flex h-[56px] items-center'>
          <FormControlLabel
            control={
              <Checkbox
                checked={invoice.printedCheck}
                disabled
                data-testid='accounting.invoice-review-dialog.printed-check-field'
              />
            }
            label='Printed Check'
          />
        </Box>
      </Box>

      <Box className='flex flex-col gap-4'>
        {invoice.expenseLines.map((line, index) => (
          <AerosTextField
            key={line.invoiceExpenseLineId}
            label='Expense Account'
            value={expenseAccountIdToLabel.get(line.glAccountId) ?? ""}
            disabled
            required
            data-testid={`accounting.invoice-review-dialog.expense-account-field-${index}`}
          />
        ))}
      </Box>
    </Box>
  )
}
